import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../assets/assetKeys';
import { VISUAL_THEME } from '../assets/visualTheme';
import { GAME_HEIGHT, GAME_WIDTH, SCENE_KEYS, SCORE_MAX_PERFECT, SCORE_TIER_VOLUNTEER_MAX, SCORE_TIERS } from '../data/gameConstants';
import { AudioSystem } from '../systems/AudioSystem';
import { SaveSystem } from '../systems/SaveSystem';
import { ResponsiveSystem } from '../systems/ResponsiveSystem';
import { Button } from '../ui/Button';
import { createMenuScrollPanel } from '../ui/MenuScrollPanel';
import { getPanelBounds, getTitleY, getModalPanelHeight, getModalFirstButtonY, getModalSubtitleY, getModalDividerY, getModalBodyTop, getStartGamePanelHeight, SCROLL_PANEL_REFERENCE_LINES } from '../ui/PanelLayout';
import { UIStyle } from '../ui/UIStyle';
import { SceneTransition } from '../utils/SceneTransition';

const PANEL_BUTTON_DEPTH = 600;

export class MainMenuScene extends Phaser.Scene {
  private activePanel?: Phaser.GameObjects.Container;
  private panelButtons: Button[] = [];
  private panelScrollCleanup?: () => void;
  private menuButtons: Button[] = [];
  private titleShadow?: Phaser.GameObjects.Text;
  private titleText?: Phaser.GameObjects.Text;
  private subtitleText?: Phaser.GameObjects.Text;
  private audio = new AudioSystem(this);

  constructor() {
    super({ key: 'MainMenuScene' });
  }

  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    SceneTransition.reveal(this);
    const layout = ResponsiveSystem.getUILayout(this);

    this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, TEXTURE_KEYS.BG_MENU)
      .setDisplaySize(GAME_WIDTH, GAME_HEIGHT);
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, VISUAL_THEME.menuDimAlpha);

    const rafflesiaLeft = this.add.image(140, 560, TEXTURE_KEYS.RAFFLESIA).setScale(layout.isCompact ? 0.55 : 0.7);
    const rafflesiaRight = this.add.image(1080, 565, TEXTURE_KEYS.RAFFLESIA).setScale(layout.isCompact ? 0.45 : 0.55);
    this.tweens.add({
      targets: [rafflesiaLeft, rafflesiaRight],
      scaleX: '+=0.04',
      scaleY: '+=0.04',
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.add.image(1020, 400, TEXTURE_KEYS.HOUSE_BUBUNGAN).setScale(0.42).setAlpha(0.9);
    this.add.image(200, 420, TEXTURE_KEYS.TREE_LARGE).setScale(0.5).setAlpha(0.85);
    this.add.image(104, 520, TEXTURE_KEYS.KAKEK_SAKTI).setScale(0.95).setAlpha(0.9);
    this.add.image(1168, 512, TEXTURE_KEYS.IBUNDO).setScale(0.96).setAlpha(0.92);
    this.add.image(642, 560, TEXTURE_KEYS.SAFE_GATHERING).setScale(0.52).setAlpha(0.42);
    this.add.image(300, 520, TEXTURE_KEYS.KERIS_1).setScale(0.76).setAngle(-18).setAlpha(0.85);

    this.titleShadow = UIStyle.titleShadow(this, GAME_WIDTH / 2, layout.titleY, 'SEISM', layout.titleSize);
    this.titleText = this.add.text(GAME_WIDTH / 2, layout.titleY, 'SEISM', {
      ...UIStyle.onBrightBg(layout.titleSize, '#fff6dd', true),
    }).setOrigin(0.5);

    this.subtitleText = this.add.text(
      GAME_WIDTH / 2,
      layout.titleY + layout.subtitleOffset,
      'Legenda Bengkulu • Simulasi Tanggap Bencana',
      { ...UIStyle.subtitleOnBrightBg(layout.subtitleSize) },
    ).setOrigin(0.5);

    const menuItems: { label: string; action: () => void }[] = [
      { label: 'Mulai Game', action: () => this.showStartGamePanel() },
      { label: 'Cara Bermain', action: () => this.showHowToPlayPanel() },
      { label: 'Panduan', action: () => this.showGuidePanel() },
      { label: 'Pengaturan', action: () => this.showSettingsPanel() },
      { label: 'Credit', action: () => this.showCreditPanel() },
    ];

    const btnSize = layout.isCompact ? UIStyle.px(19) : UIStyle.px(20);
    menuItems.forEach((item, i) => {
      const btn = new Button(this, {
        x: GAME_WIDTH / 2,
        y: layout.menuStartY + layout.buttonGap * i,
        text: item.label,
        width: layout.buttonWidth,
        height: layout.buttonHeight,
        fontSize: btnSize,
        onClick: item.action,
      });
      this.menuButtons.push(btn);
    });

    this.applyMenuLayout();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.applyMenuLayout, this);

    // Musik ambient — butuh interaksi pengguna (kebijakan autoplay browser)
    this.input.once('pointerdown', () => this.audio.playMusic('ambient'));
    this.time.delayedCall(150, this.applyMenuLayout, [], this);
  }

  private applyMenuLayout(): void {
    const layout = ResponsiveSystem.getUILayout(this);
    const cx = GAME_WIDTH / 2;

    this.titleShadow?.setPosition(cx + 2, layout.titleY + 2);
    this.titleText?.setPosition(cx, layout.titleY);
    this.subtitleText?.setPosition(cx, layout.titleY + layout.subtitleOffset);

    this.menuButtons.forEach((btn, i) => {
      btn.getContainer().setPosition(cx, layout.menuStartY + layout.buttonGap * i);
    });
  }

  shutdown(): void {
    this.scale.off(Phaser.Scale.Events.RESIZE, this.applyMenuLayout, this);
    this.closePanel({ restoreMenu: false, deferDestroy: false });
    this.menuButtons.forEach((button) => button.destroy());
    this.menuButtons = [];
  }

  private closePanel(options?: { restoreMenu?: boolean; deferDestroy?: boolean }): void {
    const restoreMenu = options?.restoreMenu ?? true;
    const deferDestroy = options?.deferDestroy ?? true;

    this.panelScrollCleanup?.();
    this.panelScrollCleanup = undefined;

    const buttons = [...this.panelButtons];
    const panel = this.activePanel;
    this.panelButtons = [];
    this.activePanel = undefined;

    if (restoreMenu) {
      this.setMenuButtonsEnabled(true);
    }

    const destroyPanel = () => {
      buttons.forEach((btn) => btn.destroy());
      panel?.destroy();
    };

    if (deferDestroy) {
      window.setTimeout(destroyPanel, 0);
    } else {
      destroyPanel();
    }
  }

  private addPanelBackButton(panelX: number, backButtonY: number, btnW: number): void {
    const layout = ResponsiveSystem.getUILayout(this);
    const btn = new Button(this, {
      x: panelX,
      y: backButtonY,
      text: 'Kembali',
      width: btnW,
      height: layout.buttonHeight,
      fontSize: layout.isCompact ? UIStyle.px(19) : UIStyle.px(20),
      depth: PANEL_BUTTON_DEPTH,
      onClick: () => this.deferClosePanel(),
    });
    this.panelButtons.push(btn);
  }

  /** Tutup panel setelah event pointer selesai — hindari crash Phaser saat destroy mid-input. */
  private deferClosePanel(): void {
    window.setTimeout(() => this.closePanel(), 0);
  }

  private createModalPanel(
    title: string,
    panelW: number,
    panelH: number,
    isCompact: boolean,
    options?: { bodyCardHeight?: number },
  ): ReturnType<typeof getPanelBounds> {
    this.activePanel = this.add.container(0, 0).setDepth(500);

    const overlay = UIStyle.createDarkOverlay(this, GAME_WIDTH, GAME_HEIGHT, 0.84);

    const bounds = getPanelBounds(this, panelW, panelH);
    const { panelX, panelY, panelTop, panelW: clampedW, panelH: clampedH } = bounds;
    const panelRect = new Phaser.Geom.Rectangle(
      panelX - clampedW / 2,
      panelY - clampedH / 2,
      clampedW,
      clampedH,
    );

    const panel = UIStyle.createModalSurface(this, panelX, panelY, clampedW, clampedH);

    const cardH = options?.bodyCardHeight ?? 0;
    const cardTop = getModalBodyTop(panelTop);
    const contentCard = cardH > 0
      ? UIStyle.createContentCard(
        this,
        panelX - clampedW / 2 + 28,
        cardTop,
        clampedW - 56,
        cardH,
      )
      : null;

    const outsideTop = this.add.rectangle(GAME_WIDTH / 2, panelRect.y / 2, GAME_WIDTH, panelRect.y, 0x000000, 0);
    const outsideBottom = this.add.rectangle(
      GAME_WIDTH / 2,
      panelRect.bottom + (GAME_HEIGHT - panelRect.bottom) / 2,
      GAME_WIDTH,
      GAME_HEIGHT - panelRect.bottom,
      0x000000,
      0,
    );
    const outsideLeft = this.add.rectangle(panelRect.x / 2, panelY, panelRect.x, clampedH, 0x000000, 0);
    const outsideRight = this.add.rectangle(
      panelRect.right + (GAME_WIDTH - panelRect.right) / 2,
      panelY,
      GAME_WIDTH - panelRect.right,
      clampedH,
      0x000000,
      0,
    );

    const titleText = this.add.text(panelX, getTitleY(panelTop), title, {
      ...UIStyle.panelTitle(isCompact ? UIStyle.px(28) : UIStyle.px(30)),
    }).setOrigin(0.5);

    const divider = this.add.rectangle(
      panelX,
      getModalDividerY(panelTop),
      clampedW - 56,
      2,
      0xe7c487,
      0.42,
    );

    const panelChildren: Phaser.GameObjects.GameObject[] = [
      overlay,
      outsideTop,
      outsideBottom,
      outsideLeft,
      outsideRight,
      panel,
      divider,
      titleText,
    ];
    if (contentCard) panelChildren.splice(6, 0, contentCard);
    this.activePanel.add(panelChildren);
    [outsideTop, outsideBottom, outsideLeft, outsideRight].forEach((outsideArea) => {
      outsideArea.setInteractive();
      outsideArea.on('pointerup', () => this.deferClosePanel());
    });

    this.setMenuButtonsEnabled(false);

    return bounds;
  }

  private setMenuButtonsEnabled(enabled: boolean): void {
    this.menuButtons.forEach((btn) => btn.setEnabled(enabled));
  }

  private showTextPanel(title: string, lines: string[]): void {
    this.closePanel();

    const layout = ResponsiveSystem.getUILayout(this);
    const panel = createMenuScrollPanel(this, {
      title,
      lines,
      isCompact: layout.isCompact,
      buttonHeight: layout.buttonHeight,
      onClose: () => this.deferClosePanel(),
    });

    this.activePanel = panel.container;
    this.panelButtons.push(panel.backButton);
    this.panelScrollCleanup = panel.destroyExtras;
    this.setMenuButtonsEnabled(false);
  }

  private showStartGamePanel(): void {
    this.closePanel();

    const layout = ResponsiveSystem.getUILayout(this);
    const panelW = layout.isCompact ? 420 : 480;
    const storageReady = SaveSystem.isStorageAvailable();
    const hasSave = storageReady && SaveSystem.hasSave();
    const summary = storageReady ? SaveSystem.getSummary() : null;
    const hasDetailedSave = hasSave && !!summary;
    const saveCardH = hasDetailedSave ? 80 : (hasSave || !storageReady ? (storageReady ? 44 : 60) : 44);
    const panelH = getStartGamePanelHeight(layout.buttonHeight, {
      hasDetailedSave,
      hasSave,
      storageReady,
    });

    const modal = this.createModalPanel('Mulai Petualangan', panelW, panelH, layout.isCompact, {
      bodyCardHeight: saveCardH > 0 ? saveCardH : undefined,
    });
    const btnW = modal.panelW - 72;
    const btnGap = layout.buttonHeight + 14;
    const buttonStartY = getModalFirstButtonY(modal.panelTop, layout.buttonHeight, saveCardH);
    const statusStyle = {
      ...UIStyle.onDarkPanel(UIStyle.px(15), '#eef2f8', true),
      align: 'center' as const,
      wordWrap: { width: panelW - 80 },
      lineSpacing: 4,
    };
    const detailStyle = {
      ...UIStyle.onDarkPanel(UIStyle.px(13), '#c8d6e8', false),
      align: 'center' as const,
      wordWrap: { width: panelW - 80 },
      lineSpacing: 3,
    };

    if (hasSave || !storageReady) {
      const statusText = hasSave
        ? `Save ditemukan — ${summary!.playerName}`
        : 'Penyimpanan nonaktif';
      const statusLineCount = hasDetailedSave ? 2 : (!storageReady ? 2 : 1);
      const subtitleY = getModalSubtitleY(modal.panelTop, saveCardH, 0, statusLineCount);
      const subtitle = this.add.text(modal.panelX, subtitleY, statusText, statusStyle).setOrigin(0.5);
      this.activePanel!.add(subtitle);

      if (hasDetailedSave && summary) {
        const lastPlayedText = this.add.text(
          modal.panelX,
          getModalSubtitleY(modal.panelTop, saveCardH, 1, 2),
          `Zona ${summary.currentZone} · ${summary.rafflesiaPoints} poin · ${this.formatSaveTime(summary.timestamp)}`,
          detailStyle,
        ).setOrigin(0.5);
        this.activePanel!.add(lastPlayedText);
      }
    } else if (storageReady) {
      const subtitleY = getModalSubtitleY(modal.panelTop, saveCardH, 0, 1);
      const subtitle = this.add.text(
        modal.panelX,
        subtitleY,
        'Belum ada save. Mulai petualangan baru?',
        statusStyle,
      ).setOrigin(0.5);
      this.activePanel!.add(subtitle);
    }

    if (!storageReady) {
      const helpText = this.add.text(
        modal.panelX,
        getModalSubtitleY(modal.panelTop, saveCardH, 1, 2),
        'Aktifkan penyimpanan browser agar progress tersimpan.',
        detailStyle,
      ).setOrigin(0.5);
      this.activePanel!.add(helpText);
    }

    const continueBtn = new Button(this, {
      x: modal.panelX,
      y: buttonStartY,
      text: 'Lanjut',
      width: btnW,
      height: layout.buttonHeight,
      fontSize: layout.isCompact ? UIStyle.px(19) : UIStyle.px(20),
      depth: PANEL_BUTTON_DEPTH,
      onClick: () => {
        if (!SaveSystem.isStorageAvailable() || !SaveSystem.hasSave()) return;
        this.resumeGame();
      },
    });
    continueBtn.setEnabled(storageReady && hasSave);
    this.panelButtons.push(continueBtn);

    const restartBtn = new Button(this, {
      x: modal.panelX,
      y: buttonStartY + btnGap,
      text: 'Mulai Baru',
      width: btnW,
      height: layout.buttonHeight,
      fontSize: layout.isCompact ? UIStyle.px(19) : UIStyle.px(20),
      depth: PANEL_BUTTON_DEPTH,
      onClick: () => {
        SaveSystem.clear();
        SceneTransition.startNextFrame(this, SCENE_KEYS.NAME_INPUT);
      },
    });
    this.panelButtons.push(restartBtn);

    this.addPanelBackButton(modal.panelX, modal.backButtonY, btnW);
  }

  private resumeGame(): void {
    if (!SaveSystem.isStorageAvailable() || !SaveSystem.hasSave()) return;
    const scene = SaveSystem.getResumeScene();
    const zone = SaveSystem.getZoneForScene(scene);
    SaveSystem.saveProgress(scene, zone);
    this.cameras.main.resetFX();
    SceneTransition.startNextFrame(this, scene);
  }

  private formatSaveTime(timestamp: number): string {
    if (!Number.isFinite(timestamp) || timestamp <= 0) return '-';
    return new Intl.DateTimeFormat('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(timestamp));
  }

  private showHowToPlayPanel(): void {
    this.showTextPanel('Cara Bermain', [
      'Ikuti petualangan Bujang Awang Tabuang menyelamatkan Ibundo Putri Rimas.',
      '',
      '— Desktop —',
      '• Gerak: WASD atau tombol panah',
      '• Interaksi: tombol E',
      '',
      '— HP / Tablet —',
      '• Analog kiri untuk bergerak',
      '• Tombol Aksi (E) di kanan',
      '• Putar perangkat landscape',
      '',
      '— Tujuan —',
      '• Kumpulkan 3 Keris Pusaka',
      '• Ikuti mitigasi bencana BMKG & BNPB',
      '• Kumpulkan Rafflesia Point',
      '• Terapkan Sekundang Setungguan',
      '',
      `Gelar akhir: ${SCORE_TIERS.HERO.min}+ Pahlawan Sekundang Setungguan, ${SCORE_TIERS.VOLUNTEER.min}–${SCORE_TIER_VOLUNTEER_MAX} Relawan Siaga, 0–${SCORE_TIERS.VOLUNTEER.min - 1} Warga Biasa. Skor sempurna: ${SCORE_MAX_PERFECT}.`,
    ]);
  }

  private showGuidePanel(): void {
    this.showTextPanel('Panduan', [
      '5 zona edukasi mitigasi bencana:',
      '',
      'Zona 1 — Pesan dari Langit',
      '• Baca papan informasi, temui Kakek Sakti',
      '• Pahami peringatan dini bencana',
      '',
      'Zona 2 — Hutan Berguncang',
      '• Bertahan saat simulasi gempa',
      '• Kumpulkan Keris Pusaka pertama',
      '',
      'Zona 3 — Rumah Bubungan Lima',
      '• Kumpulkan Keris Pusaka kedua',
      '• Isi Tas Siaga BNPB (30 item, pilih 10 wajib)',
      '• Pahami fungsi rumah panggung dari narator intro',
      '',
      'Zona 4 — Pilih Jalur Aman',
      '• Evakuasi lewat bukit, bukan tepi sungai',
      '• Kumpulkan Keris Pusaka ketiga',
      '',
      'Zona 5 — Aksi Sekundang Setungguan',
      '• Escort warga rentan ke titik kumpul',
      '• Temui Ibundo Putri Rimas',
    ]);
  }

  private showSettingsPanel(): void {
    this.closePanel();

    const layout = ResponsiveSystem.getUILayout(this);
    const panelW = layout.isCompact ? 440 : 500;
    const panelH = getModalPanelHeight(layout.buttonHeight, {
      actionButtons: 1,
      bodyBlockHeight: 36,
    });
    const muted = this.audio.isMuted();

    const modal = this.createModalPanel('Pengaturan', panelW, panelH, layout.isCompact);
    const btnW = modal.panelW - 72;
    const bodyTop = getModalBodyTop(modal.panelTop);
    const statusY = bodyTop + 18;
    const muteBtnY = getModalFirstButtonY(modal.panelTop, layout.buttonHeight, 36);

    const statusLabel = this.add.text(
      modal.panelX,
      statusY,
      `Suara efek: ${muted ? 'Mati' : 'Aktif'}`,
      { ...UIStyle.panelBody(UIStyle.px(18)) },
    ).setOrigin(0.5);

    const muteBtn = new Button(this, {
      x: modal.panelX,
      y: muteBtnY,
      text: muted ? 'Nyalakan Suara' : 'Matikan Suara',
      width: btnW,
      height: layout.buttonHeight,
      fontSize: layout.isCompact ? UIStyle.px(19) : UIStyle.px(20),
      depth: PANEL_BUTTON_DEPTH,
      onClick: () => {
        const nowMuted = this.audio.toggleMute();
        statusLabel.setText(`Suara efek: ${nowMuted ? 'Mati' : 'Aktif'}`);
        muteBtn.setText(nowMuted ? 'Nyalakan Suara' : 'Matikan Suara');
      },
    });
    this.panelButtons.push(muteBtn);

    this.activePanel!.add(statusLabel);
    this.addPanelBackButton(modal.panelX, modal.backButtonY, btnW);
  }

  private showCreditPanel(): void {
    this.showTextPanel('Credit', [...SCROLL_PANEL_REFERENCE_LINES]);
  }
}
